import { FC } from "react";
import { useUserSettings } from "../../context/userSettingsContext";
import { useI18n } from "../../hooks/useTranslations";
import NumberError from "../ui/NumberError";
import Table from "../ui/Table";
import styles from "./Notifications.module.css";

type NotificationCategory = {
  label: string;
  color: "red" | "yellow";
  count: number;
  to: string;
};

type NotificationsProps = {
  onSelect: (to: string) => void;
};

const Notifications: FC<NotificationsProps> = ({ onSelect }) => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const notifications = userSettings.notifications;

  const carPurchaseExpired = notifications?.carPurchaseExpired ?? 0;
  const carPurchaseArrivingSoon = notifications?.carPurchaseArrivingSoon ?? 0;
  const dueDateExpired = notifications?.dueDateExpired ?? 0;
  const dueDateExpiringSoon = notifications?.dueDateExpiringSoon ?? 0;
  const carLeasingExpired = notifications?.carLeasingExpired ?? 0;
  const carLeasingExpiringSoon = notifications?.carLeasingExpiringSoon ?? 0;

  const categories: NotificationCategory[] = [];

  if (userSettings.permissions.carPurchases.read) {
    categories.push({
      label: i18n.t.carOrders.plural,
      color: carPurchaseExpired > 0 ? "red" : "yellow",
      count: carPurchaseExpired + carPurchaseArrivingSoon,
      to: "/admin/car_purchases/arriving_soon",
    });
  }

  if (userSettings.permissions.users.read) {
    categories.push({
      label: i18n.t.users.plural,
      color: "red",
      count: notifications?.usersMissingCostCenterCount ?? 0,
      to: "/admin/users?with_import_errors=1",
    });
  }

  if (userSettings.permissions.carTasks.read) {
    categories.push({
      label: i18n.t.carTasks.plural,
      color: "red",
      count: notifications?.taskToDo ?? 0,
      to: "/admin/car_tasks/only_to_do",
    });
  }

  if (userSettings.permissions.dueDates.read) {
    categories.push({
      label: i18n.t.dueDates.plural,
      color: dueDateExpired > 0 ? "red" : "yellow",
      count: dueDateExpired > 0 ? dueDateExpired : dueDateExpiringSoon,
      to: dueDateExpired > 0 ? "/admin/due_dates/expired" : "/admin/due_dates/expiring_soon",
    });
  }

  if (userSettings.permissions.carLeasings.read) {
    categories.push({
      label: i18n.t.carLeasings.plural,
      color: carLeasingExpired > 0 ? "red" : "yellow",
      count: carLeasingExpired + carLeasingExpiringSoon,
      to: "/admin/car_leasings",
    });
  }

  const visibleCategories = categories
    .filter((category) => category.count > 0)
    .sort((firstCategory, secondCategory) => secondCategory.count - firstCategory.count);

  return (
    <Table<NotificationCategory>
      head={
        <>
          <Table.Th title={i18n.t.common.type} fieldName="type" />
          <Table.Th title={i18n.t.common.count} fieldName="number" align="center" />
        </>
      }
      items={visibleCategories}
      onRowClick={(category) => onSelect(category.to)}
      renderRow={(category) => (
        <>
          <td>{category.label}</td>
          <td align="center">
            <NumberError number={category.count} color={category.color} className={styles.count} />
          </td>
        </>
      )}
    />
  );
};

export default Notifications;
